import { useEffect, useMemo, useState } from "react";
import {
    SimpleForm,
    TextInput,
    DateInput,
    SaveButton,
    Toolbar,
    required,
    useCreate,
    useDataProvider,
    useNotify,
    useRedirect,
    useRefresh,
    useTranslate,
} from "react-admin";
import { Box, Button, TextField as MuiTextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { RaCreateView } from "../components/views/RaCreateView";
import { RaSelectInput } from "shared/src/components/inputs/Ra/RaSelectInput";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { removeChecksum } from "shared/src/utils/removeChecksum";

export const SkuInspectionCreate = (props) => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const dataProvider = useDataProvider();
    const [create] = useCreate();
    const [labelNumber, setLabelNumber] = useState("");
    const [sku, setSku] = useState(null);
    const query = new URLSearchParams(window.location.hash.split("?")[1] || "");
    const initialSkuId = query.get("sku");
    const returnTo = query.get("returnTo");
    const initialType = query.get("type") || "";
    const defaultValues = useMemo(() => ({
        type: initialType,
        result: "passed",
        date: new Date(),
    }), [initialType]);

    async function lookupSku() {
        try {
            if (initialSkuId) {
                const response = await dataProvider.getOne("skus", { id: initialSkuId });
                setSku(response.data);
                setLabelNumber(response.data.labelNumber);
                return;
            }

            const normalized = removeChecksum(labelNumber);
            if (!normalized) {
                notify("equip.skuRequired", { type: "warning" });
                return;
            }

            const response = await dataProvider.getList("skus", {
                filter: { labelNumber: normalized },
                pagination: { page: 1, perPage: 1 },
                sort: { field: "id", order: "DESC" },
            });
            setSku(response.data[0] || null);
            if (!response.data[0]) {
                notify("equip.skuNotFound", { type: "warning" });
            }
        } catch (e) {
            handleHttpError(e, notify);
        }
    }

    useEffect(() => {
        if (initialSkuId && !sku) {
            lookupSku();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSkuId]);

    const submit = (data) => {
        if (!sku) {
            notify("equip.skuRequired", { type: "warning" });
            return;
        }

        create(
            "skuInspections",
            {
                data: {
                    ...data,
                    labelNumber: undefined,
                    sku,
                },
            },
            {
                onError: (e) => handleHttpError(e, notify),
                onSuccess: () => {
                    refresh();
                    redirect(
                        returnTo === "skuInspections"
                            ? "/skuInspections"
                            : `/skus/${sku.id}/show`
                    );
                },
            }
        );
    };

    return (
        <RaCreateView {...props}>
            <SimpleForm
                onSubmit={submit}
                toolbar={<SkuInspectionToolbar skuSelected={!!sku} />}
                defaultValues={defaultValues}
            >
                {!initialSkuId && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                        }}
                    >
                        <MuiTextField
                            label={translate("equip.labelNumber")}
                            value={labelNumber}
                            onChange={(event) => setLabelNumber(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    lookupSku();
                                }
                            }}
                        />
                        <Button onClick={lookupSku} startIcon={<SearchIcon />}>
                            {translate("equip.search")}
                        </Button>
                    </Box>
                )}
                {sku && (
                    <Typography variant="body2">
                        {translate("equip.sku")}: {sku.labelNumber}
                    </Typography>
                )}
                <RaSelectInput
                    source="type"
                    label="equip.inspectionType"
                    validate={[required()]}
                    choices={[
                        { id: "vde", name: "equip.skuInspectionType_vde" },
                        { id: "uvv", name: "equip.skuInspectionType_uvv" },
                    ]}
                />
                <RaSelectInput
                    source="result"
                    label="equip.inspectionResult"
                    choices={[
                        { id: "passed", name: "equip.skuInspectionResult_passed" },
                        { id: "failed", name: "equip.skuInspectionResult_failed" },
                    ]}
                />
                <DateInput source="date" label="equip.date" />
                <TextInput source="comment" label="equip.comment" multiline fullWidth />
            </SimpleForm>
        </RaCreateView>
    );
};

const SkuInspectionToolbar = ({ skuSelected }) => (
    <Toolbar>
        <SaveButton disabled={!skuSelected} />
    </Toolbar>
);
